import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  HiArrowDownTray,
  HiBanknotes,
  HiOutlineSparkles,
  HiPlus,
  HiReceiptPercent,
  HiShoppingBag,
  HiTag,
} from "react-icons/hi2";
import apiClient from "../api/apiClient";
import PayoutStatusCard from "../components/PayOutStatusCard";
import { handleErrors } from "../modules/handleErrors";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import {
  formatCurrencyAmount,
  getAvailableBalance,
  getCashOutBalance,
  getEarnings,
  getMinCashout,
  getPendingBalance,
  getStatsCurrency,
} from "../utils/currencyUtils";
import { getActiveSale } from "../utils/saleUtils";
import WarningModal from "../modals/WarningModal";
import RecentSalesList from "../components/RecentSalesList";
import {
  BarList,
  DashboardHero,
  ErrorState,
  Panel,
  RangeTabs,
  StatTile,
  TileSkeleton,
  TrendChart,
  bucketLabel,
  bucketTitle,
  formatCount,
  formatCountTick,
  formatMoney,
  formatMoneyTick,
  previousLabel,
  toRows,
  useActivity,
} from "../components/dashboard-kit";

const SALE_PERCENTAGE_OPTIONS = [10, 15, 20, 25, 30, 40, 50];

const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getToday = () => formatLocalDate(new Date());

const addDays = (dateStr, days) => {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  return formatLocalDate(d);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { userid } = useSelector((state) => state.auth);
  const { profiledata, fetchDataprofile } = useIndexPageData();
  const [range, setRange] = useState("30d");
  const [saleItems, setSaleItems] = useState([]);
  const [chartdata, setchartdata] = useState({});
  const [loader, setLoader] = useState(false);
  const [saleLoader, setSaleLoader] = useState(false);
  const [endSaleLoader, setEndSaleLoader] = useState(false);
  const [showDeleteSaleModal, setShowDeleteSaleModal] = useState(false);
  const [saleForm, setSaleForm] = useState({
    sale_percentage: "",
    sale_start_date: "",
    sale_end_date: "",
  });
  const activity = useActivity("/stats/seller-activity/", range);

  const hasCurrentSale =
    profiledata?.sale_percentage != null && profiledata?.sale_percentage !== "";
  const saleInfo = profiledata
    ? getActiveSale(profiledata)
    : { active: false, percentage: null };

  useEffect(() => {
    if (profiledata) {
      setSaleForm((prev) => ({
        ...prev,
        sale_percentage: profiledata.sale_percentage ?? "",
        sale_start_date: profiledata.sale_start_date
          ? profiledata.sale_start_date.slice(0, 10)
          : "",
        sale_end_date: profiledata.sale_end_date
          ? profiledata.sale_end_date.slice(0, 10)
          : "",
      }));
    }
  }, [profiledata]);

  const today = getToday();

  const handleSaleChange = (key, value) => {
    setSaleForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "sale_start_date" && next.sale_end_date) {
        const minEnd = addDays(value, 1);
        if (next.sale_end_date < minEnd) next.sale_end_date = "";
      }
      return next;
    });
  };

  const minEndDate = saleForm.sale_start_date
    ? addDays(saleForm.sale_start_date, 1)
    : today;

  const saveSale = async (e) => {
    e.preventDefault();
    if (!userid) return;
    setSaleLoader(true);
    try {
      const formData = new FormData();
      if (saleForm.sale_percentage !== "" && saleForm.sale_percentage != null) {
        formData.append("sale_percentage", saleForm.sale_percentage);
      }
      if (saleForm.sale_start_date) {
        formData.append("sale_start_date", saleForm.sale_start_date);
      }
      if (saleForm.sale_end_date) {
        formData.append("sale_end_date", saleForm.sale_end_date);
      }
      const result = await apiClient.put(`/register/${userid}/`, formData);
      setSaleLoader(false);
      if (!result.ok) return handleErrors(result.data);
      toast.success(
        "Sale settings saved. All your mockups will be discounted in the selected period.",
      );
      fetchDataprofile();
    } catch (err) {
      setSaleLoader(false);
      toast.error("Failed to save sale settings");
    }
  };

  const clearSale = async () => {
    if (!userid) return;
    setEndSaleLoader(true);
    try {
      const formData = new FormData();
      formData.append("sale_percentage", "");
      formData.append("sale_start_date", "");
      formData.append("sale_end_date", "");
      const result = await apiClient.put(`/register/${userid}/`, formData);
      if (!result.ok) return handleErrors(result.data);
      setSaleForm({
        sale_percentage: "",
        sale_start_date: "",
        sale_end_date: "",
      });
      toast.success("Sale ended. Discount has been removed from all mockups.");
      fetchDataprofile();
    } catch (err) {
      toast.error("Failed to end sale");
    } finally {
      setEndSaleLoader(false);
    }
  };

  const isSaleFormComplete =
    saleForm.sale_percentage &&
    saleForm.sale_start_date &&
    saleForm.sale_end_date;

  const fetchSales = async () => {
    const result = await apiClient.get("/sales/?data=r");
    if (result.ok) setSaleItems(result.data.results);
  };

  const fetchStats = async () => {
    const result = await apiClient.get("/stats/");
    if (result.ok) setchartdata(result.data);
  };

  const performcashOut = async () => {
    try {
      setLoader(true);
      const result = await apiClient.post("/sales/create-transfer/", {
        amount: getCashOutBalance(chartdata),
      });
      setLoader(false);
      if (!result.ok) return handleErrors(result.data);
      toast.success("Cash out succecfully");
      fetchSales();
      fetchStats();
    } catch (error) {
      setLoader(false);
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    fetchSales();
    fetchStats();
  }, []);

  useEffect(() => {
    if (userid) fetchDataprofile();
  }, [userid]);

  const sellerName = profiledata?.full_name?.trim();
  const statsCurrency = getStatsCurrency(chartdata);
  const { data, loading, refreshing, error, reload } = activity;
  const usd = (v) => formatMoney(v, data?.currency || "USD");
  const rows = useMemo(() => toRows(data, bucketLabel, bucketTitle), [data]);
  const totals = data?.totals || {};
  const previous = data?.previous || {};
  const prev = previousLabel(range);
  const avg = (t) => (t?.sales ? t.earnings / t.sales : 0);

  const topItems = (data?.top_listings || []).map((l) => ({
    id: l.id,
    label: l.title,
    value: l.earnings,
    image: l.thumbnail,
    to: `/mockup?id=${l.id}`,
    meta: `${formatCount(l.sales)} sale${l.sales === 1 ? "" : "s"} · ${formatCount(l.views)} views`,
  }));

  return (
    <>
      <div className="dk-page dk-stack seller-dashboard">
        <DashboardHero
          eyebrow="Seller dashboard"
          name={sellerName}
          subtitle="Here's how your mockups are performing. Track earnings, payouts and promotions in one place."
          actions={
            <>
              <Link to="/seller/my-listings" className="dk-btn dk-btn--light">
                <HiPlus aria-hidden="true" /> Add a listing
              </Link>
              <Link to="/seller/my-shop" className="dk-btn dk-btn--glass">
                <HiShoppingBag aria-hidden="true" /> View my shop
              </Link>
              <Link to="/seller/all-sales" className="dk-btn dk-btn--glass">
                All sales
              </Link>
            </>
          }
          figureLabel={`Lifetime earnings (${statsCurrency})`}
          figure={formatCurrencyAmount(getEarnings(chartdata), statsCurrency)}
          figureNote={
            <>
              {formatCurrencyAmount(getAvailableBalance(chartdata), statsCurrency)} available ·{" "}
              {chartdata?.total_listing ?? "—"} active listing{chartdata?.total_listing === 1 ? "" : "s"}
            </>
          }
        />

        <div className="dk-toolbar dk-rise" style={{ "--i": 1 }}>
          <div>
            <h2 className="dk-toolbar__title">Performance</h2>
            <p className="dk-toolbar__sub">Earnings shown in USD after Stripe fees and Mockee's commission.</p>
          </div>
          <RangeTabs value={range} onChange={setRange} />
        </div>

        {error && !data && <ErrorState message={error} onRetry={reload} />}
        {loading && !data ? (
          <TileSkeleton count={4} />
        ) : (
          data && (
            <div className="dk-tiles">
              <StatTile index={2} label="Earnings" icon={HiBanknotes} value={totals.earnings} format={usd} current={totals.earnings} previous={previous.earnings} previousLabel={prev} trend={data.series.earnings} />
              <StatTile index={3} label="Sales" icon={HiReceiptPercent} value={totals.sales} format={formatCount} current={totals.sales} previous={previous.sales} previousLabel={prev} trend={data.series.sales} />
              <StatTile index={4} label="Mockup downloads" icon={HiArrowDownTray} value={totals.downloads} format={formatCount} current={totals.downloads} previous={previous.downloads} previousLabel={prev} trend={data.series.downloads} />
              <StatTile index={5} label="Avg. earnings per sale" icon={HiOutlineSparkles} value={avg(totals)} format={usd} current={avg(totals)} previous={avg(previous)} previousLabel={prev} />
            </div>
          )
        )}

        <div className="dk-grid">
          <Panel className="dk-span-8" index={6} title="Earnings" subtitle={`Your share of each sale, ${prev.replace("previous ", "last ")}`}>
            <TrendChart
              rows={rows}
              refreshing={refreshing}
              series={[{ key: "earnings", label: "Earnings", kind: "area" }]}
              formatValue={usd}
              formatTick={(v) => formatMoneyTick(v, data?.currency || "USD")}
              caption="Earnings by period"
              emptyText="No sales in this period yet — share your shop to get the first one."
            />
          </Panel>
          <Panel
            className="dk-span-4"
            index={7}
            title="Top mockups"
            subtitle="By earnings in this period"
            action={
              <Link to="/seller/my-listings" className="dk-link">
                All listings
              </Link>
            }
          >
            <BarList items={topItems} formatValue={usd} emptyText="Your best sellers will appear here." />
          </Panel>

          <Panel className="dk-span-7" index={8} title="Sales & downloads" subtitle="Purchases of your mockups and designs exported from them">
            <TrendChart
              rows={rows}
              refreshing={refreshing}
              series={[
                { key: "sales", label: "Sales", kind: "bar" },
                { key: "downloads", label: "Downloads", kind: "bar" },
              ]}
              formatValue={formatCount}
              formatTick={formatCountTick}
              integer
              caption="Sales and downloads by period"
            />
          </Panel>
          <div className="dk-span-5 dk-rise" style={{ "--i": 9 }}>
            <PayoutStatusCard
              performcashOut={performcashOut}
              userBalance={getAvailableBalance(chartdata)}
              pendingBalance={getPendingBalance(chartdata)}
              minCashout={getMinCashout(chartdata)}
              currency={statsCurrency}
              loading={loader}
            />
          </div>

          <Panel
            className="dk-span-12"
            index={10}
            title="Run a sale"
            subtitle="Apply a discount across all your mockups for a limited time."
            action={
              hasCurrentSale ? (
                <span className={`dk-badge ${saleInfo.active ? "dk-badge--good" : "dk-badge--muted"}`}>
                  <HiTag aria-hidden="true" />
                  {saleInfo.active ? "Active" : "Scheduled / Ended"}
                </span>
              ) : null
            }
          >
            {hasCurrentSale && (
              <div className="seller-dashboard__sale-status">
                <div className="seller-dashboard__sale-status-info">
                  <p className="seller-dashboard__sale-status-label mb-1">Current sale</p>
                  <p className="seller-dashboard__sale-status-value mb-0">
                    {profiledata?.sale_percentage}% off
                    {profiledata?.sale_start_date || profiledata?.sale_end_date
                      ? ` • ${profiledata?.sale_start_date ? new Date(profiledata.sale_start_date).toLocaleDateString() : "—"} – ${profiledata?.sale_end_date ? new Date(profiledata.sale_end_date).toLocaleDateString() : "—"}`
                      : ""}
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={saveSale}>
              <div className="seller-dashboard__sale-form">
                <div className="seller-dashboard__field">
                  <label className="seller-dashboard__field-label" htmlFor="sale-percentage">
                    Discount %
                  </label>
                  <select
                    id="sale-percentage"
                    className="form-select"
                    value={saleForm.sale_percentage}
                    onChange={(e) => handleSaleChange("sale_percentage", e.target.value)}
                  >
                    <option value="">Select discount</option>
                    {SALE_PERCENTAGE_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}%
                      </option>
                    ))}
                  </select>
                </div>
                <div className="seller-dashboard__field">
                  <label className="seller-dashboard__field-label" htmlFor="sale-start-date">
                    Start date
                  </label>
                  <input
                    id="sale-start-date"
                    type="date"
                    className="form-control"
                    min={today}
                    value={saleForm.sale_start_date}
                    onChange={(e) => handleSaleChange("sale_start_date", e.target.value)}
                  />
                </div>
                <div className="seller-dashboard__field">
                  <label className="seller-dashboard__field-label" htmlFor="sale-end-date">
                    End date
                  </label>
                  <input
                    id="sale-end-date"
                    type="date"
                    className="form-control"
                    min={minEndDate}
                    value={saleForm.sale_end_date}
                    onChange={(e) => handleSaleChange("sale_end_date", e.target.value)}
                  />
                </div>
              </div>

              {!isSaleFormComplete && (
                <p className="seller-dashboard__sale-hint mb-0">
                  Choose a discount percentage and date range, then click{" "}
                  {hasCurrentSale ? "Update sale" : "Run sale"}.
                </p>
              )}

              <div className="seller-dashboard__sale-actions">
                <button
                  type="submit"
                  className="dk-btn dk-btn--accent"
                  disabled={!isSaleFormComplete || saleLoader || endSaleLoader}
                >
                  {saleLoader ? "Saving..." : hasCurrentSale ? "Update sale" : "Run sale"}
                </button>
                {hasCurrentSale && (
                  <button
                    type="button"
                    className="dk-btn dk-btn--ghost"
                    disabled={saleLoader || endSaleLoader}
                    onClick={() => setShowDeleteSaleModal(true)}
                  >
                    {endSaleLoader ? "Ending..." : "End sale"}
                  </button>
                )}
              </div>
            </form>
          </Panel>

          <Panel
            className="dk-span-12"
            index={11}
            title="Recent transactions"
            action={
              <button type="button" className="dk-btn dk-btn--ghost" onClick={() => navigate("/seller/all-sales")}>
                View all sales
              </button>
            }
          >
            <div className="seller-dashboard__transactions-scroll px-1">
              <RecentSalesList items={saleItems} emptyMessage="Your sales is empty." />
            </div>
          </Panel>
        </div>
      </div>

      <WarningModal
        showModal={showDeleteSaleModal}
        modalClose={() => setShowDeleteSaleModal(false)}
        setConfirmClose={() => {
          setShowDeleteSaleModal(false);
          clearSale();
        }}
        text="Are you sure you want to end this sale? This will remove the discount from all your mockups."
      />
    </>
  );
};

export default Dashboard;
